import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Loader2, CheckCircle2, AlertCircle, ArrowLeft, Eye, EyeOff } from 'lucide-react'
import {
  useForgotPassword,
  useResetPassword,
  useAcceptInvite,
} from '../../modules/auth/hooks/useAuth'
import { ROUTES } from '../../router/routes'

// ─── ForgotPasswordPage ───────────────────────────────────────────────────────
const forgotSchema = z.object({ email: z.string().email('Email invalide') })

export function ForgotPasswordPage() {
  const { mutate, isPending, isSuccess, error } = useForgotPassword()
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(forgotSchema),
  })

  return (
    <div>
      <Link
        to={ROUTES.LOGIN}
        className="flex items-center gap-1 text-[var(--tx3)] hover:text-[var(--brand-dark)] text-sm mb-8 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" /> Retour
      </Link>

      <h2 className="text-2xl font-bold text-[var(--brand-dark)] mb-2">Mot de passe oublié</h2>
      <p className="text-[var(--tx3)] mb-8 text-sm">
        Entrez votre email et nous vous enverrons un lien de réinitialisation.
      </p>

      {isSuccess ? (
        <div className="flex items-start gap-3 bg-[#ECFDF5] border border-[rgba(16,185,129,0.25)] text-[#059669] rounded-xl p-4 text-sm">
          <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Email envoyé</p>
            <p className="text-[#059669]/70 mt-1">
              Si cet email est enregistré, vous recevrez un lien valide 1 heure.
            </p>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit((d) => mutate(d.email))} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-[var(--tx2)] mb-2">Adresse email</label>
            <input
              {...register('email')}
              type="email"
              placeholder="vous@nexuscorp.sn"
              className="w-full bg-[var(--surface-2)] border border-[var(--bd)] text-[var(--brand-dark)] placeholder-[var(--tx3)]
                         rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-blue-500
                         focus:ring-1 focus:ring-[var(--blue)] transition-colors"
            />
            {errors.email && (
              <p className="text-[#DC2626] text-xs mt-1">{errors.email.message as string}</p>
            )}
          </div>

          {error && (
            <div className="flex items-center gap-2 bg-[#FEF2F2] border border-[rgba(239,68,68,0.25)] text-[#DC2626] rounded-xl p-3 text-sm">
              <AlertCircle className="w-4 h-4 shrink-0" />
              Une erreur est survenue.
            </div>
          )}

          <button
            type="submit"
            disabled={isPending}
            className="w-full bg-[var(--blue)] hover:bg-[var(--blue-d)] disabled:opacity-50 text-white
                       font-semibold rounded-xl py-3 text-sm transition-colors flex items-center justify-center gap-2"
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Envoi…
              </>
            ) : (
              'Envoyer le lien'
            )}
          </button>
        </form>
      )}
    </div>
  )
}

export default ForgotPasswordPage

// ─── ResetPasswordPage ────────────────────────────────────────────────────────
const resetSchema = z
  .object({
    password: z.string().min(8, 'Minimum 8 caractères'),
    confirm: z.string(),
  })
  .refine((d) => d.password === d.confirm, {
    message: 'Les mots de passe ne correspondent pas',
    path: ['confirm'],
  })

export function ResetPasswordPage() {
  const [showPwd, setShowPwd] = useState(false)
  const [params] = useSearchParams()
  const token = params.get('token') || ''
  const { mutate, isPending, error } = useResetPassword()
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(resetSchema),
  })

  if (!token)
    return (
      <div className="text-center text-[var(--tx3)]">
        <p>Lien invalide.</p>
        <Link
          to={ROUTES.FORGOT_PASSWORD}
          className="text-[var(--blue-d)] hover:underline mt-2 inline-block"
        >
          Demander un nouveau lien
        </Link>
      </div>
    )

  return (
    <div>
      <h2 className="text-2xl font-bold text-[var(--brand-dark)] mb-2">Nouveau mot de passe</h2>
      <p className="text-[var(--tx3)] mb-8 text-sm">
        Choisissez un mot de passe sécurisé (8 caractères minimum).
      </p>

      <form
        onSubmit={handleSubmit((d) => mutate({ token, password: d.password }))}
        className="space-y-5"
      >
        {(['password', 'confirm'] as const).map((field) => (
          <div key={field}>
            <label className="block text-sm font-medium text-[var(--tx2)] mb-2">
              {field === 'password' ? 'Nouveau mot de passe' : 'Confirmer'}
            </label>
            <div className="relative">
              <input
                {...register(field)}
                type={showPwd ? 'text' : 'password'}
                placeholder="••••••••"
                className="w-full bg-[var(--surface-2)] border border-[var(--bd)] text-[var(--brand-dark)] placeholder-[var(--tx3)]
                           rounded-xl px-4 py-3 pr-11 text-sm focus:outline-none focus:border-blue-500
                           focus:ring-1 focus:ring-[var(--blue)] transition-colors"
              />
              {field === 'password' && (
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--tx3)]"
                >
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              )}
            </div>
            {errors[field] && (
              <p className="text-[#DC2626] text-xs mt-1">{errors[field]?.message as string}</p>
            )}
          </div>
        ))}

        {error && (
          <div className="flex items-center gap-2 bg-[#FEF2F2] border border-[rgba(239,68,68,0.25)] text-[#DC2626] rounded-xl p-3 text-sm">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {(error as any)?.response?.data?.message || 'Lien expiré ou invalide.'}
          </div>
        )}

        <button
          type="submit"
          disabled={isPending}
          className="w-full bg-[var(--blue)] hover:bg-[var(--blue-d)] disabled:opacity-50 text-white
                     font-semibold rounded-xl py-3 text-sm transition-colors flex items-center justify-center gap-2"
        >
          {isPending ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Réinitialisation…
            </>
          ) : (
            'Réinitialiser'
          )}
        </button>
      </form>
    </div>
  )
}

// ─── AcceptInvitePage ─────────────────────────────────────────────────────────
export function AcceptInvitePage() {
  const [showPwd, setShowPwd] = useState(false)
  const [params] = useSearchParams()
  const token = params.get('token') || ''
  const { mutate, isPending, error } = useAcceptInvite()
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(resetSchema),
  })

  if (!token) return <div className="text-center text-[var(--tx3)]">Lien d'invitation invalide.</div>

  return (
    <div>
      <h2 className="text-2xl font-bold text-[var(--brand-dark)] mb-2">Activez votre compte</h2>
      <p className="text-[var(--tx3)] mb-8 text-sm">
        Créez votre mot de passe pour rejoindre NexusCorp ERP.
      </p>

      <form
        onSubmit={handleSubmit((d) => mutate({ token, password: d.password }))}
        className="space-y-5"
      >
        {(['password', 'confirm'] as const).map((field) => (
          <div key={field}>
            <label className="block text-sm font-medium text-[var(--tx2)] mb-2">
              {field === 'password' ? 'Créer un mot de passe' : 'Confirmer'}
            </label>
            <div className="relative">
              <input
                {...register(field)}
                type={showPwd ? 'text' : 'password'}
                placeholder="••••••••"
                className="w-full bg-[var(--surface-2)] border border-[var(--bd)] text-[var(--brand-dark)] placeholder-[var(--tx3)]
                           rounded-xl px-4 py-3 pr-11 text-sm focus:outline-none focus:border-blue-500
                           focus:ring-1 focus:ring-[var(--blue)] transition-colors"
              />
              {field === 'password' && (
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--tx3)]"
                >
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              )}
            </div>
            {errors[field] && (
              <p className="text-[#DC2626] text-xs mt-1">{errors[field]?.message as string}</p>
            )}
          </div>
        ))}

        {error && (
          <div className="flex items-center gap-2 bg-[#FEF2F2] border border-[rgba(239,68,68,0.25)] text-[#DC2626] rounded-xl p-3 text-sm">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {(error as any)?.response?.data?.message || 'Lien expiré.'}
          </div>
        )}

        <button
          type="submit"
          disabled={isPending}
          className="w-full bg-[var(--blue)] hover:bg-[var(--blue-d)] disabled:opacity-50 text-white
                     font-semibold rounded-xl py-3 text-sm transition-colors flex items-center justify-center gap-2"
        >
          {isPending ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Activation…
            </>
          ) : (
            'Activer mon compte'
          )}
        </button>
      </form>
    </div>
  )
}
